import { ContentType } from '@/@types/common'
import type { User } from '@/@types/user'
import {
	COLLECT_POST,
	COLLECT_REPOST,
	DELETE_POST,
	DELETE_REPOST,
	LIKE_POST,
	LIKE_REPOST,
	PIN_POST,
	SPONSOR_HIDE_POST,
	SPONSOR_HIDE_REPOST,
	SPONSOR_RECOVER_POST,
	SPONSOR_RECOVER_REPOST,
	UNCOLLECT_POST,
	UNCOLLECT_REPOST,
	UNLIKE_POST,
	UNLIKE_REPOST,
	UNPIN_POST,
} from '@/constants/api'
import { cachePost, retrievePost } from '@/utils/fetchers/post'
import { currentUserQueryOptions } from '@/utils/fetchers/user'
import queryClient from '@/utils/queryClient'
import { post } from '@/utils/request'
import { useMutation } from '@tanstack/react-query'
import { cloneDeep, isNil, set, update } from 'lodash-es'
import toast from 'react-hot-toast'

const TOGGLE_LIKE_ENDPOINT_MAP = {
	[ContentType.REPOST]: {
		UNLIKE: UNLIKE_REPOST,
		LIKE: LIKE_REPOST,
	},
	[ContentType.POST]: {
		UNLIKE: UNLIKE_POST,
		LIKE: LIKE_POST,
	},
} as Record<ContentType.POST | ContentType.REPOST, Record<'LIKE' | 'UNLIKE', string>>

export function usePostLikeToggle(id: string) {
	const { mutateAsync: toggleLike } = useMutation({
		mutationFn: async () => {
			const postData = retrievePost(id)
			if (isNil(postData)) {
				return Promise.reject(new Error('No valid postData'))
			}

			const endpoint =
				TOGGLE_LIKE_ENDPOINT_MAP[postData.type as ContentType.POST | ContentType.REPOST][
					postData.liked ? 'LIKE' : 'UNLIKE'
				]

			return post(endpoint, {
				id: postData.id,
			}).then((res) => res.data)
		},
		onMutate: async () => {
			const previousPost = retrievePost(id)

			const nextPost = cloneDeep(previousPost)

			if (nextPost) {
				if (nextPost.liked) {
					update(nextPost, 'likeCount', (likeCount: number) => likeCount - 1)
					set(nextPost, 'liked', false)
				} else {
					update(nextPost, 'likeCount', (likeCount: number) => likeCount + 1)
					set(nextPost, 'liked', true)

					const currentUser = queryClient.getQueryData<User>(currentUserQueryOptions.queryKey)
					const isLikingSelf = nextPost.user?.id === currentUser?.id

					if (isLikingSelf) {
						toast('😏', {
							position: 'bottom-center',
						})
					}
				}
				cachePost(nextPost)
			}

			return { prevData: previousPost }
		},
		onError: (err, _vars, context) => {
			if (context?.prevData) {
				cachePost(context.prevData)
			}
		},
	})
	return {
		toggleLike,
	}
}

export function usePostPinToggle(id: string) {
	const { mutateAsync: togglePin } = useMutation({
		mutationFn: async () => {
			const postData = retrievePost(id)
			if (isNil(postData)) {
				return Promise.reject(new Error('No valid postData'))
			}
			return post(postData.pinned?.personalUpdate ? PIN_POST : UNPIN_POST, {
				type: postData.type,
				id: postData.id,
			}).then((res) => res.data)
		},
		onMutate: async () => {
			const previousPost = retrievePost(id)
			const nextPost = cloneDeep(previousPost)

			if (nextPost) {
				update(nextPost, 'pinned.personalUpdate', (pinned) => !pinned)
				cachePost(nextPost)
			}

			return { prevData: previousPost }
		},
		onError: (err, _vars, context) => {
			if (context?.prevData) {
				cachePost(context.prevData)
			}
		},
	})
	return {
		togglePin,
	}
}

export function usePostDelete(id: string) {
	const { mutateAsync: deletePost } = useMutation({
		mutationFn: async () => {
			const postData = retrievePost(id)
			if (isNil(postData)) {
				return Promise.reject(new Error('No valid postData'))
			}
			const api = postData.type === ContentType.POST ? DELETE_POST : DELETE_REPOST
			return post(api, {
				id: postData.id,
			}).then((res) => res.data)
		},
		onMutate: async () => {
			const previousPost = retrievePost(id)
			const nextPost = cloneDeep(previousPost)

			if (nextPost) {
				set(nextPost, 'deleted', true)
				cachePost(nextPost)
			}

			return { prevData: previousPost }
		},
		onError: (err, _vars, context) => {
			if (context?.prevData) {
				cachePost(context.prevData)
			}
		},
	})
	return {
		deletePost,
	}
}

const TOGGLE_COLLECT_ENDPOINT_MAP = {
	[ContentType.REPOST]: {
		UNCOLLECT: UNCOLLECT_REPOST,
		COLLECT: COLLECT_REPOST,
	},
	[ContentType.POST]: {
		UNCOLLECT: UNCOLLECT_POST,
		COLLECT: COLLECT_POST,
	},
} as Record<ContentType.POST | ContentType.REPOST, Record<'UNCOLLECT' | 'COLLECT', string>>

export function usePostCollectToggle(id: string) {
	const { mutateAsync: toggleCollect } = useMutation({
		mutationFn: async () => {
			const postData = retrievePost(id)

			if (isNil(postData)) {
				return Promise.reject(new Error('No valid postData'))
			}
			const endpoint =
				TOGGLE_COLLECT_ENDPOINT_MAP[postData.type as ContentType.POST | ContentType.REPOST][
					postData.collected ? 'COLLECT' : 'UNCOLLECT'
				]
			return post(endpoint, {
				id: postData.id,
			}).then((res) => res.data)
		},
		onMutate: async () => {
			const previousPost = retrievePost(id)
			const nextPost = cloneDeep(previousPost)

			if (nextPost) {
				update(nextPost, 'collected', (prev) => !prev)
				cachePost(nextPost)
			}

			return { prevData: previousPost }
		},
		onError: (err, _vars, context) => {
			if (context?.prevData) {
				cachePost(context.prevData)
			}
		},
	})
	return {
		toggleCollect,
	}
}

const TOGGLE_SPONSOR_ENDPOINT_MAP = {
	[ContentType.REPOST]: {
		HIDE: SPONSOR_HIDE_REPOST,
		RECOVER: SPONSOR_RECOVER_REPOST,
	},
	[ContentType.POST]: {
		HIDE: SPONSOR_HIDE_POST,
		RECOVER: SPONSOR_RECOVER_POST,
	},
} as Record<ContentType.POST | ContentType.REPOST, Record<'HIDE' | 'RECOVER', string>>

export function usePostSponsorVisibilityToggle(id: string) {
	const { mutateAsync: togglePostVisibility } = useMutation({
		mutationFn: async () => {
			const postData = retrievePost(id)
			if (isNil(postData)) {
				return Promise.reject(new Error('No valid postData'))
			}
			const endpoint =
				TOGGLE_SPONSOR_ENDPOINT_MAP[postData.type as ContentType.POST | ContentType.REPOST][
					postData.isPrivate ? 'HIDE' : 'RECOVER'
				]
			return post(endpoint, {
				id: postData.id,
			}).then((res) => res.data)
		},
		onMutate: async () => {
			const previousPost = retrievePost(id)
			const nextPost = cloneDeep(previousPost)

			if (nextPost) {
				if (nextPost.isPrivate) {
					set(nextPost, 'isPrivate', false)
					set(nextPost, 'privatizableStatus', 'ENABLE_HIDE')
				} else {
					set(nextPost, 'isPrivate', true)
					set(nextPost, 'privatizableStatus', 'ENABLE_RECOVER')
				}
				cachePost(nextPost)
			}

			return { prevData: previousPost }
		},
		onError: (err, _vars, context) => {
			if (context?.prevData) {
				cachePost(context.prevData)
			}
		},
	})
	return {
		togglePostVisibility,
	}
}
